import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import betslip from '@gin-betting/betslip';
import { createSelector, createStructuredSelector } from 'reselect';

import { usePureSelector } from 'hooks';

const {
    selectors: {
        selectBetslipBetIds,
        selectBetslipBets,
        selectChosenBetslipBetId,
        selectInitialBetslipBet,
    },
    actions: { choseBetslipBetId, deleteBetslipBet },
} = betslip;

interface Output {
    betslipBetIds: ReturnType<typeof selectBetslipBetIds>;
    chosenBetslipBetId: ReturnType<typeof selectChosenBetslipBetId>;
}

const selectInitialBetslipBetId = createSelector(
    selectInitialBetslipBet,
    betslipBet => betslipBet?.id
);

const selectOutdatedBetslipBetIds = createSelector(
    selectBetslipBets,
    betslipBets =>
        betslipBets
            .filter(({ betslipOddIds }) => betslipOddIds.count() === 0)
            .keySeq()
);

const selectActiveBetslipBetIds = createSelector(
    selectBetslipBetIds,
    selectOutdatedBetslipBetIds,
    (betslipBetIds, outdatedBetslipBetIds) =>
        betslipBetIds.subtract(outdatedBetslipBetIds).slice(-6)
);

const selectProps = createStructuredSelector({
    betslipBetIds: selectActiveBetslipBetIds,
    outdatedBetslipBetIds: selectOutdatedBetslipBetIds,
    chosenBetslipBetId: selectChosenBetslipBetId,
    initialBetslipBetId: selectInitialBetslipBetId,
});

const useBetslipList = (): Output => {
    const dispatch = useDispatch();

    const {
        betslipBetIds,
        chosenBetslipBetId,
        initialBetslipBetId,
        outdatedBetslipBetIds,
    } = usePureSelector(s => selectProps(s, {}));

    useEffect(() => {
        if (!chosenBetslipBetId && initialBetslipBetId) {
            dispatch(choseBetslipBetId({ betslipBetId: initialBetslipBetId }));
        }
    }, [dispatch, chosenBetslipBetId, initialBetslipBetId]);

    useEffect(() => {
        outdatedBetslipBetIds.forEach(betslipBetId => {
            if (initialBetslipBetId === betslipBetId) return;

            dispatch(deleteBetslipBet({ betslipBetId }));
        });
    }, [dispatch, outdatedBetslipBetIds, initialBetslipBetId]);

    return {
        // TODO: uncomment next line if product provide a blank page design. https://jira.syneforge.com/browse/GINFE-2589
        // betslipBetIds: betslipBetIds,
        betslipBetIds:
            betslipBetIds.count() === 0 && initialBetslipBetId
                ? betslipBetIds.add(initialBetslipBetId)
                : betslipBetIds,
        chosenBetslipBetId,
    };
};

export default useBetslipList;
